// Per-publication SEO metadata. Kept free of DOM / asset imports so it can be
// used both by the React app (client-side head updates) and by vite.config.ts
// (build-time static HTML for /publication/:slug).
import { SEO } from './seo-config'

export type PublicationSeo = {
  slug: string
  title: string
  description: string
  canonical: string
  jsonLd: Record<string, unknown>
}

const PERSON_ID = `${SEO.portfolioURL}/#person`

export function publicationSeo(slug: string): PublicationSeo | undefined {
  const p = SEO.publications.find((x) => x.slug === slug)
  if (!p) return undefined

  const canonical = `${SEO.portfolioURL}/publication/${p.slug}/`
  const title = `${p.title} | ${SEO.name}`

  const article = {
    '@type': 'ScholarlyArticle',
    '@id': `${SEO.portfolioURL}/#${p.id}`,
    headline: p.title.length > 110 ? `${p.title.slice(0, 107)}…` : p.title,
    name: p.title,
    description: p.description,
    abstract: p.description,
    keywords: p.keywords.join(', '),
    inLanguage: 'en',
    url: canonical,
    mainEntityOfPage: canonical,
    image: SEO.ogImage,
    author: p.authors.map((a) => (a === SEO.name ? { '@id': PERSON_ID } : { '@type': 'Person', name: a })),
    isPartOf: { '@type': 'PublicationEvent', name: p.conference },
    ...(p.publisher ? { publisher: { '@type': 'Organization', name: p.publisher } } : {}),
    ...(p.datePublished ? { datePublished: p.datePublished } : { creativeWorkStatus: 'Accepted' }),
    ...(p.doi
      ? {
          identifier: { '@type': 'PropertyValue', propertyID: 'DOI', value: p.doi },
          sameAs: [`https://doi.org/${p.doi}`, ...(p.publisherLink ? [p.publisherLink] : [])],
        }
      : {}),
  }

  const breadcrumbs = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SEO.portfolioURL}/` },
      { '@type': 'ListItem', position: 2, name: 'Publications', item: `${SEO.portfolioURL}/publications/` },
      { '@type': 'ListItem', position: 3, name: p.title, item: canonical },
    ],
  }

  return {
    slug: p.slug,
    title,
    description: p.description,
    canonical,
    jsonLd: { '@context': 'https://schema.org', '@graph': [article, breadcrumbs] },
  }
}

export const publicationSlugs = SEO.publications.map((p) => p.slug)
